// The experiment chart (brief §19).
//
// Form: a line over time, because the question is change-over-time. Hand-rolled SVG
// rather than a charting library — the requirements are specific (a shaded baseline
// band, a phase marker, a rolling average, tap-a-point detail) and every library
// makes those awkward while adding weight to a PWA that must open instantly.
//
// Chart rules applied here:
//   · one y-axis, never two — a second measure gets its own chart
//   · raw readings are dots, the 7-day rolling average is the 2px line; both are the
//     same metric, so they share one hue and are told apart by mark and by legend
//   · gaps in logging break the line instead of being interpolated across
//   · grid and axes are recessive; text wears ink tokens, never the series colour
//   · a tap/hover layer is present by default, with a crosshair and a readout

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Confounder, MetricAnalysis } from '../lib/api.ts';
import { shortDate } from '../lib/format.ts';

interface Props {
  analysis: MetricAnalysis;
  baselineStart: string | null;
  baselineEnd: string | null;
  interventionStart: string | null;
  end: string;
  confounders?: Confounder[];
  height?: number;
}

const PAD = { top: 16, right: 12, bottom: 26, left: 30 };

function dateRange(from: string, to: string): string[] {
  const out: string[] = [];
  const toMs = (d: string) => Date.UTC(Number(d.slice(0, 4)), Number(d.slice(5, 7)) - 1, Number(d.slice(8, 10)));
  for (let ms = toMs(from); ms <= toMs(to); ms += 86_400_000) {
    out.push(new Date(ms).toISOString().slice(0, 10));
  }
  return out;
}

/** Nice round tick values across a range, at most `count` of them. */
function ticks(min: number, max: number, count = 4): number[] {
  if (max <= min) return [min];
  const raw = (max - min) / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? mag * 10;
  const out: number[] = [];
  for (let v = Math.ceil(min / step) * step; v <= max + 1e-9; v += step) out.push(Number(v.toFixed(6)));
  return out;
}

export function MetricChart({
  analysis, baselineStart, baselineEnd, interventionStart, end, confounders = [], height = 220,
}: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(360);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  useEffect(() => {
    const node = wrapRef.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(Math.max(240, entry.contentRect.width));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const model = useMemo(() => {
    const first = baselineStart ?? interventionStart ?? analysis.series[0]?.local_date ?? end;
    const days = dateRange(first, end);
    const byDate = new Map(analysis.series.map((p) => [p.local_date, p]));

    const values = analysis.series.map((p) => p.value).filter((v): v is number => v !== null);
    // Prefer the metric's declared scale so 0–10 charts always show the whole scale —
    // auto-fitting a symptom score to its own range exaggerates every wobble.
    const lo = analysis.metric.min_value ?? (values.length ? Math.min(...values) : 0);
    const hi = analysis.metric.max_value ?? (values.length ? Math.max(...values) : 10);
    const min = lo === hi ? lo - 1 : lo;
    const max = lo === hi ? hi + 1 : hi;

    const plotW = width - PAD.left - PAD.right;
    const plotH = height - PAD.top - PAD.bottom;
    const x = (i: number) => PAD.left + (days.length <= 1 ? plotW / 2 : (i / (days.length - 1)) * plotW);
    const y = (v: number) => PAD.top + plotH - ((v - min) / (max - min)) * plotH;

    const confoundersByDate = new Map<string, string[]>();
    for (const c of confounders) {
      confoundersByDate.set(c.local_date, [...(confoundersByDate.get(c.local_date) ?? []), c.type]);
    }

    const points = days.map((date, i) => {
      const p = byDate.get(date);
      return {
        date, i,
        cx: x(i),
        value: p?.value ?? null,
        rolling: p?.rolling ?? null,
        phase: p?.phase ?? null,
        confounders: confoundersByDate.get(date) ?? [],
      };
    });

    // Build the rolling line in segments so a run of unlogged days leaves a real gap
    // rather than a straight line implying data that was never collected.
    const segments: string[] = [];
    let current: string[] = [];
    for (const p of points) {
      if (p.rolling === null) {
        if (current.length > 1) segments.push(current.join(' '));
        current = [];
        continue;
      }
      current.push(`${current.length === 0 ? 'M' : 'L'}${p.cx.toFixed(1)},${y(p.rolling).toFixed(1)}`);
    }
    if (current.length > 1) segments.push(current.join(' '));

    const bandX = baselineStart && baselineEnd
      ? { from: x(days.indexOf(baselineStart)), to: x(Math.max(0, days.indexOf(baselineEnd))) }
      : null;
    const markerX = interventionStart && days.includes(interventionStart) ? x(days.indexOf(interventionStart)) : null;

    return { days, points, segments, min, max, x, y, plotW, plotH, bandX, markerX };
  }, [analysis, baselineStart, baselineEnd, interventionStart, end, confounders, width, height]);

  const pick = useCallback((clientX: number) => {
    const node = wrapRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const local = clientX - rect.left;
    let best = 0;
    let bestDistance = Infinity;
    model.points.forEach((p, i) => {
      const d = Math.abs(p.cx - local);
      if (d < bestDistance) { bestDistance = d; best = i; }
    });
    setActiveIndex(best);
  }, [model.points]);

  const active = activeIndex === null ? null : model.points[activeIndex];
  const yTicks = ticks(model.min, model.max, 4);
  const xLabelIndices = model.days.length <= 1 ? [0]
    : [0, Math.floor((model.days.length - 1) / 2), model.days.length - 1];

  const seriesColor = 'var(--color-series-1)';

  return (
    <figure className="m-0">
      <div
        ref={wrapRef}
        className="relative touch-pan-y"
        onPointerDown={(e) => pick(e.clientX)}
        onPointerMove={(e) => { if (e.buttons > 0 || e.pointerType === 'mouse') pick(e.clientX); }}
        onPointerLeave={() => setActiveIndex(null)}
      >
        <svg width={width} height={height} role="img"
          aria-label={`${analysis.metric.name} over time. ${analysis.description}`}>
          {/* Baseline period — a wash, labelled, so the comparison periods are
              visually distinct without being colour-coded like a data series. */}
          {model.bandX && (
            <>
              <rect
                x={model.bandX.from} y={PAD.top}
                width={Math.max(1, model.bandX.to - model.bandX.from)} height={model.plotH}
                fill="var(--color-surface-2)"
              />
              <text
                x={model.bandX.from + 4} y={PAD.top + 11}
                className="text-[10px]" fill="var(--color-muted)"
              >
                Baseline
              </text>
            </>
          )}

          {yTicks.map((t) => (
            <g key={t}>
              <line
                x1={PAD.left} x2={width - PAD.right} y1={model.y(t)} y2={model.y(t)}
                stroke="var(--color-hairline)" strokeWidth={1}
              />
              <text
                x={PAD.left - 6} y={model.y(t) + 3.5} textAnchor="end"
                className="text-[10px] tabular-nums" fill="var(--color-muted)"
              >
                {t}
              </text>
            </g>
          ))}

          {/* Where treatment began. */}
          {model.markerX !== null && (
            <>
              <line
                x1={model.markerX} x2={model.markerX} y1={PAD.top} y2={PAD.top + model.plotH}
                stroke="var(--color-line)" strokeWidth={1.5} strokeDasharray="3 3"
              />
              <text
                x={model.markerX + 4} y={PAD.top + 11}
                className="text-[10px]" fill="var(--color-ink-2)"
              >
                Treatment
              </text>
            </>
          )}

          {model.segments.map((d, i) => (
            <path key={i} d={d} fill="none" stroke={seriesColor} strokeWidth={2}
              strokeLinecap="round" strokeLinejoin="round" />
          ))}

          {model.points.map((p) => p.value === null ? null : (
            <circle
              key={p.date} cx={p.cx} cy={model.y(p.value)} r={3}
              fill={seriesColor} fillOpacity={0.45}
            />
          ))}

          {/* Days with a confounder get a tick under the axis — the chart should show
              that something else was going on, not hide it in a separate screen. */}
          {model.points.map((p) => p.confounders.length === 0 ? null : (
            <line
              key={`c-${p.date}`} x1={p.cx} x2={p.cx}
              y1={PAD.top + model.plotH + 2} y2={PAD.top + model.plotH + 6}
              stroke="var(--color-warning)" strokeWidth={2}
            />
          ))}

          {active && (
            <>
              <line
                x1={active.cx} x2={active.cx} y1={PAD.top} y2={PAD.top + model.plotH}
                stroke="var(--color-ink-2)" strokeWidth={1}
              />
              {active.value !== null && (
                <circle cx={active.cx} cy={model.y(active.value)} r={5.5}
                  fill={seriesColor} stroke="var(--color-surface)" strokeWidth={2} />
              )}
            </>
          )}

          <line
            x1={PAD.left} x2={width - PAD.right} y1={PAD.top + model.plotH} y2={PAD.top + model.plotH}
            stroke="var(--color-line)" strokeWidth={1}
          />

          {xLabelIndices.map((i) => {
            const p = model.points[i];
            if (!p) return null;
            return (
              <text
                key={p.date} x={p.cx} y={height - 8}
                textAnchor={i === 0 ? 'start' : i === model.days.length - 1 ? 'end' : 'middle'}
                className="text-[10px]" fill="var(--color-muted)"
              >
                {shortDate(p.date)}
              </text>
            );
          })}
        </svg>

        {active && (
          <div
            className="absolute top-0 pointer-events-none card px-2.5 py-2 shadow-lg text-xs min-w-32"
            style={{
              left: Math.min(Math.max(0, active.cx - 60), Math.max(0, width - 140)),
            }}
          >
            <p className="font-medium">{shortDate(active.date)}</p>
            <p className="tabular-nums mt-0.5">
              {analysis.metric.name}: <strong>{active.value ?? '—'}</strong>
              {analysis.metric.unit ? ` ${analysis.metric.unit}` : ''}
            </p>
            {active.rolling !== null && (
              <p className="text-ink-2 tabular-nums">7-day average: {active.rolling.toFixed(1)}</p>
            )}
            {active.phase && <p className="text-muted capitalize">{active.phase}</p>}
            {active.confounders.length > 0 && (
              <p className="text-warning mt-0.5">⚑ {active.confounders.join(', ').replace(/_/g, ' ')}</p>
            )}
          </div>
        )}
      </div>

      {/* Two marks on the plot, so both are named. Text stays in ink tokens; the
          swatch beside it carries identity. */}
      <figcaption className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-ink-2">
        <span className="inline-flex items-center gap-1.5">
          <svg width="14" height="8" aria-hidden="true"><circle cx="7" cy="4" r="3" fill={seriesColor} fillOpacity={0.45} /></svg>
          Daily reading
        </span>
        <span className="inline-flex items-center gap-1.5">
          <svg width="14" height="8" aria-hidden="true"><line x1="0" y1="4" x2="14" y2="4" stroke={seriesColor} strokeWidth="2" /></svg>
          7-day average
        </span>
        {confounders.length > 0 && (
          <span className="inline-flex items-center gap-1.5">
            <svg width="14" height="8" aria-hidden="true"><line x1="7" y1="0" x2="7" y2="8" stroke="var(--color-warning)" strokeWidth="2" /></svg>
            Something else going on
          </span>
        )}
      </figcaption>
    </figure>
  );
}

/**
 * The table view of the same data.
 *
 * Not decoration: it is the accessible equivalent of the plot, and the relief for
 * series colours that sit below 3:1 against the light surface.
 */
export function MetricTable({ analysis }: { analysis: MetricAnalysis }) {
  const rows = analysis.series.filter((p) => p.value !== null);
  if (rows.length === 0) return <p className="text-sm text-muted">No readings yet.</p>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <caption className="sr-only">{analysis.metric.name} readings by day</caption>
        <thead>
          <tr className="text-left text-xs text-muted border-b border-hairline">
            <th scope="col" className="py-1.5 pr-3 font-medium">Date</th>
            <th scope="col" className="py-1.5 pr-3 font-medium">Phase</th>
            <th scope="col" className="py-1.5 text-right font-medium">{analysis.metric.name}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.local_date} className="border-b border-hairline last:border-0">
              <td className="py-1.5 pr-3 tabular-nums">{shortDate(p.local_date)}</td>
              <td className="py-1.5 pr-3 capitalize text-ink-2">{p.phase}</td>
              <td className="py-1.5 text-right tabular-nums font-medium">{p.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
